"use client";

import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Monogram,
  Skeleton,
  TextInput,
} from "@hisani/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronRight, Crown, PlusCircle } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useState, type FormEvent } from "react";
import { Link } from "../../../i18n/navigation";
import { loginPath } from "../../../i18n/paths";
import { apiErrorMessage, requestApiJson } from "../../api-client";
import { useAuth } from "../../auth-client";
import { StableListSchema, StableSummarySchema } from "./stable-contracts";

const stableQueryKeys = {
  list: ["stable", "list"] as const,
};

export function StablesWorkspace() {
  const t = useTranslations("Stable");
  const auth = useAuth();
  const locale = useLocale();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    if (auth.status === "anonymous") {
      window.location.replace(loginPath(locale, "/stable"));
    }
  }, [auth.status, locale]);

  const stablesQuery = useQuery({
    queryKey: stableQueryKeys.list,
    queryFn: () => requestApiJson("/api/v1/stable/stables", StableListSchema),
    enabled: auth.status === "authenticated",
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mx-auto w-full min-w-0 max-w-6xl px-4 pt-0 pb-4 sm:px-6 sm:py-6 lg:px-8">
        <header className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-heading-large text-primary">
              {t("myStables")}
            </h1>
            <p className="text-body mt-1 max-w-xl text-secondary">
              {t("myStablesDescription")}
            </p>
          </div>
          <Button
            type="button"
            variant="accent"
            className="min-h-12 shrink-0"
            onClick={() => setShowCreateDialog(true)}
          >
            <PlusCircle className="me-2 size-4" aria-hidden="true" />
            {t("createStable")}
          </Button>
        </header>

        <section className="mt-4" aria-label={t("myStables")}>
          {auth.status !== "authenticated" || stablesQuery.isPending ? (
            <ul
              role="status"
              aria-label={t("loading")}
              className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-surface"
            >
              {Array.from({ length: 3 }, (_, index) => (
                <li
                  key={index}
                  className="flex min-h-20 items-center gap-3 px-4 py-3"
                >
                  <Skeleton className="size-10 shrink-0 rounded-full" />
                  <span className="grid flex-1 gap-2">
                    <Skeleton className="h-4 w-2/5" />
                    <Skeleton className="h-3 w-1/3" />
                  </span>
                  <Skeleton className="size-5 shrink-0" />
                </li>
              ))}
            </ul>
          ) : stablesQuery.isError ? (
            <div
              role="alert"
              className="rounded-lg border border-border-subtle bg-surface p-4"
            >
              <p className="text-body font-semibold text-danger">
                {t("stablesLoadError")}
              </p>
              <Button
                type="button"
                variant="secondary"
                className="mt-4"
                onClick={() => void stablesQuery.refetch()}
              >
                {t("retry")}
              </Button>
            </div>
          ) : stablesQuery.data.stables.length === 0 ? (
            <EmptyState
              icon={Building2}
              title={t("noStables")}
              description={t("noStablesDescription")}
            />
          ) : (
            <ul className="divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-surface">
              {stablesQuery.data.stables.map((membership) => (
                <li key={membership.stable.id}>
                  <Link
                    href={`/stable/stable?id=${membership.stable.id}`}
                    aria-label={t("openStableLabel", {
                      stable: membership.stable.name,
                    })}
                    className="group flex min-h-20 items-center gap-3 px-4 py-3 transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus"
                  >
                    <Monogram name={membership.stable.name} />
                    <div className="min-w-0 flex-1">
                      <h2
                        dir="auto"
                        className="break-words text-base font-semibold leading-6 text-primary [overflow-wrap:anywhere]"
                      >
                        {membership.stable.name}
                      </h2>
                      <p className="text-body-small mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-secondary">
                        <span>{t(`entityRole.${membership.entityRole}`)}</span>
                        {!membership.isEntityOwner ? null : (
                          <span className="inline-flex items-center gap-1 font-semibold text-brand-ink">
                            <Crown className="size-3.5" aria-hidden="true" />
                            {t("entityOwner")}
                          </span>
                        )}
                      </p>
                    </div>
                    <ChevronRight
                      className="size-5 shrink-0 text-tertiary transition-transform group-hover:translate-x-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <CreateStableDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={() => {
          setShowCreateDialog(false);
          void queryClient.invalidateQueries({
            queryKey: stableQueryKeys.list,
          });
        }}
      />
    </div>
  );
}

function CreateStableDialog({
  open,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const t = useTranslations("Stable");
  const [validationError, setValidationError] = useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (name: string) =>
      requestApiJson("/api/v1/stable/stables", StableSummarySchema, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          origin: window.location.origin,
        },
        body: JSON.stringify({ name }),
      }),
    onSuccess: onCreated,
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = new FormData(event.currentTarget).get("name");
    if (typeof name !== "string" || name.trim().length === 0) {
      setValidationError(t("stableNameRequired"));
      const input = event.currentTarget.elements.namedItem("name");
      if (input instanceof HTMLElement) input.focus();
      return;
    }
    setValidationError(null);
    mutation.mutate(name.trim());
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      title={t("createStable")}
      description={t("createStableDescription")}
      closeLabel={t("cancel")}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            form="create-stable-form"
            variant="accent"
            loading={mutation.isPending}
          >
            {mutation.isPending ? t("creating") : t("createStable")}
          </Button>
        </>
      }
    >
      <form id="create-stable-form" onSubmit={submit} noValidate>
        <Field
          label={t("stableName")}
          error={
            validationError ??
            (mutation.error === null
              ? undefined
              : apiErrorMessage(mutation.error, t("createError")))
          }
        >
          {(field) => (
            <TextInput
              {...field}
              name="name"
              aria-required="true"
              maxLength={120}
              autoComplete="off"
              data-dialog-autofocus
              onChange={() => setValidationError(null)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
